import React, { useEffect, useState, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import httpRequest from "src/helpers/httpRequest";
import {
  availabilityList,
  CURRENCY,
  ImageBaseUrl,
  QueryPath,
} from "src/lib/constans";
import { toast } from "react-toastify";
import {
  convertMonthsToReadableFormat,
  convertToKebabCase,
  getHeaderHeight,
  parseQueryParams,
} from "src/helpers/helperFunctions";
import SideTabList from "src/Components/Pharmacy/SideTabList";
import qs from "qs";
import { cityList } from "src/Config/CityList";
import { IDiseaseItem, IDropDownArray } from "src/types/GlobalInterfaces";
import CustomBreadCrub from "src/Components/Pharmacy/CustomBreadCrub";
import DoctorWhislistCard from "src/Components/WebsiteComponents/HomePage/Cards/DoctorWhislistCard";
import DoctorViewCard from "src/Components/WebsiteComponents/HomePage/Cards/DoctorViewCard";
import { capitalize } from "@mui/material";
import NoItemUI from "src/Components/NoItemUI";
import { AppDispatch, RootState } from "src/Store/store";
import { useDispatch, useSelector } from "react-redux";
import { useQueryParams } from "src/hooks/useQueryParams";
import InfinteScrollWrapper from "src/Components/GlobalComponents/Widget/InfinteScrollWrapper";
import { removeAllDisLike, updateDisLike } from "src/Store/disLike";

const appointmentTypeOptions: IDropDownArray[] = [
  { value: "online", label: "Online Appointment", id: "online" },
  { value: "physical", label: "In-Clinic Appointment", id: "physical" },
];

const ConsultADoctorPage = () => {
  const navigate = useNavigate();
  const { isLogged, userDetails } = useSelector(
    (state: RootState) => state.appReducer
  );
  const [breadCrumbData, setBreadCrumbData] = useState<{ label: string }>({
    label: "",
  });
  const [allSpecialists, setAllSpecialists] = useState<any[]>([]);
  const [specializationLoader, setSpecializationLoader] =
    useState<boolean>(false);
  const [doctors, setDoctors] = useState<any[]>([]);
  const [doctorsLoader, setDoctorsLoader] = useState<boolean>(false);
  const [paginated, setPaginated] = useState<any>({
    currentPage: 1,
    totalPages: 1,
  });
  const [diseasStates, setDiseasStates] = useState<{
    loader: boolean;
    allDisease: IDropDownArray[];
  }>({
    loader: false,
    allDisease: [],
  });

  //   get specialization from query params
  const { getParam } = useQueryParams();
  const specialization = getParam(QueryPath.specialization);
  const specializationName = getParam(QueryPath.specializationName);
  const search = getParam(QueryPath.search);
  const { doctorDisLike } = useSelector((state: RootState) => state.disLike);
  //   Query Params
  const [queryParams, setQueryParams] = useState<any>({
    specialization: [],
    page: 1,
    limit: 10,
    location: [],
    appointmentType: "",
    availability: [],
    diseases: [],
    search: search || "",
  });

  const dispatch = useDispatch<AppDispatch>();
  
  // Debouncing refs
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const searchDebounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const debounceDelay = 500; // 500ms delay for filters
  const searchDebounceDelay = 800; // 800ms delay for search

  const getAllSpecialists = async () => {
    setSpecializationLoader(true);
    const { res, err } = await httpRequest<
      {
        _id: string;
        name: string;
        SpecailImage: string;
      }[]
    >({
      path: "/provider/special/get",
    });
    if (res) {
      setAllSpecialists(
        res.map((item) => ({
          id: item._id,
          label: item.name,
          value: item._id,
        }))
      );
    } else {
      toast(err, { type: "error" });
    }
    setSpecializationLoader(false);
  };

  const fetchDoctors = async (params: any, isLoadMore = false) => {
    // Cancel previous request if exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new abort controller
    abortControllerRef.current = new AbortController();
    
    let q = {
      specialId: params?.specialization,
      limit: params?.limit,
      page: params?.page,
      name: params?.search,
      city: params?.location,
      diseasesId: params?.diseases,
      days: params?.availability,
      appointmentType: params?.appointmentType,
    };
    setDoctorsLoader(true);
    const { res, err } = await httpRequest<{
      documents: any[];
      paginated: any;
    }>({
      path: `/provider/all/doctor?${qs.stringify(q)}`,
    });
    if (res) {
      let updatedData = res.documents.map((v) => ({
        ...v,
        specializationDetails: v?.specializationDetails?.sort(
          (a: any, b: any) => (q.specialId?.includes(a?._id) ? -1 : 1)
        ),
      }));
      
      // Only append for load more, replace for new searches
      if (isLoadMore) {
        setDoctors((prev) => {
          // Prevent duplicates by checking existing IDs
          const existingIds = new Set(prev.map(doctor => doctor._id));
          const newData = updatedData.filter(doctor => !existingIds.has(doctor._id));
          return [...prev, ...newData];
        });
      } else {
        setDoctors(updatedData);
      }
      
      setPaginated(res.paginated);
      dispatch(removeAllDisLike("doctorDisLike"));
    } else {
      toast(err, { type: "error" });
    }
    setDoctorsLoader(false);
  };

  const fetchAllDisease = async () => {
    setDiseasStates((prev) => ({ ...prev, loader: true }));
    const { res, err } = await httpRequest<IDiseaseItem[]>({
      path: `/provider/diseases/get`,
    });
    if (res) {
      setDiseasStates((prev) => ({
        ...prev,
        allDisease: res?.map((v) => ({
          label: v?.name,
          value: v?._id,
          id: v?._id,
        })),
      }));
    } else {
      toast(err, { type: "error" });
    }
    setDiseasStates((prev) => ({ ...prev, loader: false }));
  };

  useEffect(() => {
    getAllSpecialists();
    fetchAllDisease();
  }, []);

  // Cleanup debounce timeout on component unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (searchDebounceTimeoutRef.current) {
        clearTimeout(searchDebounceTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Debounced version of changeQueryParams for filters
  const debouncedFetchDoctors = useCallback((params: any) => {
    // Clear existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    // Set new timeout
    debounceTimeoutRef.current = setTimeout(() => {
      console.log("Debounced API call for filters:", params);
      fetchDoctors(params, false); // false = replace data, not append
    }, debounceDelay);
  }, []);

  // Debounced version for search
  const debouncedSearchDoctors = useCallback((params: any) => {
    // Clear existing timeout
    if (searchDebounceTimeoutRef.current) {
      clearTimeout(searchDebounceTimeoutRef.current);
    }
    
    // Set new timeout
    searchDebounceTimeoutRef.current = setTimeout(() => {
      console.log("Debounced API call for search:", params);
      fetchDoctors(params, false); // false = replace data, not append
    }, searchDebounceDelay);
  }, []);

  // Separate function for search with different debouncing
  const handleSearchChange = useCallback((searchValue: string) => {
    changeQueryParams(
      { search: searchValue },
      true,
      true,
      true // isSearch = true
    );
  }, []);

  const changeQueryParams = (
    params: Partial<typeof queryParams>,
    updateData = true,
    reset = true,
    isSearch = false
  ) => {
    let updatedParams = { ...queryParams, ...params };
    setQueryParams(updatedParams);
    if (reset) {
      setPaginated({
        currentPage: 1,
        totalPages: 1,
      });
      updatedParams.page = 1;
      setDoctors([]);
    }
    if (updateData) {
      // Use different debouncing for search vs filters
      if (isSearch) {
        debouncedSearchDoctors(updatedParams);
      } else {
        debouncedFetchDoctors(updatedParams);
      }
    }
  };

  useEffect(() => {
    if (specialization) {
      changeQueryParams({
        specialization: specialization?.split(",").map((v: string) => v.trim()),
        search: search || "",
      });
      setBreadCrumbData({ label: specializationName || "" });
    } else {
      setBreadCrumbData({ label: "" });
      changeQueryParams({
        specialization: [],
        search: search || "",
      });
    }
  }, [specialization, search]);

  useEffect(() => {
    console.log("Doctor DisLike Changed In Whishlist", doctorDisLike);
    if (doctorDisLike.length > 0) {
      let updatedStatusData = doctors.map((v) => {
        if (doctorDisLike.find((i) => i === v?.userId)) {
          return {
            ...v,
            isLiked: false,
          };
        }
        return v;
      });
      setDoctors(updatedStatusData);
      dispatch(removeAllDisLike("doctorDisLike"));
    }
  }, [doctorDisLike]);


  const GetDoctorPrice = (feesArray : any , availability : any) => {
    if (feesArray.length > 0) {
      return Math.min(...feesArray);
    }
    return availability?.fees || 0;
  }

  return (
    <div className="w-full bg-banner-white-image">
      <div className="view-area-width flex gap-1 py-3 relative">
        <div
          className="p-[2px] product-side-bar-width custom-ai-scroll-bar !sticky overflow-y-auto"
          style={{
            height: `calc(100vh - ${getHeaderHeight()}px - 16px)`,
            top: `${getHeaderHeight() + 12}px`,
          }}
        >
          <div className="w-full box-design bg-white">
            <SideTabList
              collapsable={true}
              isSelectedHighlight={true}
              title="Location"
              List={cityList.map((item) => ({
                label: item.label,
                id: item.value,
                value: item.value,
              }))}
              onListClick={(item) => {
                let data = queryParams.location || [];
                let find = data.find((tm: any) => tm === item.value);
                if (find) {
                  data = data.filter((tm: any) => tm !== item.value);
                } else {
                  data.push(item.value);
                }

                console.log("Location data ===> ", data);

                changeQueryParams({
                  location: data,
                });
              }}
              showCheckBox={true}
              showRadio={false}
              loading={specializationLoader}
              loadingText="Loading Location"
              selectedItem={queryParams.location}
            />
            <SideTabList
              collapsable={true}
              isSelectedHighlight={true}
              title="Availability"
              List={availabilityList.map((item) => ({
                label: item.label,
                value: item.value,
                id: item.value,
              }))}
              onListClick={(item) => {
                let data = queryParams.availability || [];
                let find = data.find((tm: any) => tm === item.value);
                if (find) {
                  data = data.filter((tm: any) => tm !== item.value);
                } else {
                  data.push(item.value);
                }

                changeQueryParams({
                  availability: data,
                });
              }}
              showCheckBox={true}
              showRadio={false}
              loading={specializationLoader}
              loadingText="Loading Availability"
              selectedItem={queryParams.availability}
            />
            <SideTabList
              collapsable={true}
              isSelectedHighlight={true}
              defaultCollapsed={false}
              title="Specialization"
              List={allSpecialists}
              onListClick={(item) => {
                let data = queryParams.specialization;
                let find = data?.find((tm: any) => tm === item.id);
                if (find) {
                  data = data.filter((tm: any) => tm !== item.id);
                } else {
                  data.push(item.id);
                }

                let q = {
                  [QueryPath.specialization]: data,
                  [QueryPath.specializationName]: allSpecialists
                    .filter((tm) => data.includes(tm.id))
                    .map((tm: any) => tm?.label),
                  // [QueryPath.search]: search,
                };

                navigate(`/consult-a-doctor?${parseQueryParams(q)}`);
              }}
              showCheckBox={true}
              showRadio={false}
              loading={specializationLoader}
              loadingText="Loading Specialization"
              selectedItem={queryParams.specialization}
            />
            <SideTabList
              collapsable={true}
              isSelectedHighlight={true}
              title="Disease"
              List={diseasStates.allDisease.map((item) => ({
                label: item.label,
                value: item.value,
                id: item.value,
              }))}
              onListClick={(item) => {
                let data = queryParams.diseases || [];
                let find = data.find((tm: any) => tm === item.value);
                if (find) {
                  data = data.filter((tm: any) => tm !== item.value);
                } else {
                  data.push(item.value);
                }

                changeQueryParams({
                  diseases: data,
                });
              }}
              showCheckBox={true}
              showRadio={false}
              loading={diseasStates.loader}
              loadingText="Loading Disease"
              selectedItem={queryParams.diseases}
            />
            <SideTabList
              collapsable={true}
              isSelectedHighlight={true}
              title="Appointment Type"
              List={appointmentTypeOptions.map((item) => ({
                label: item.label,
                value: item.value,
                id: item.value,
              }))}
              onListClick={(item) => {
                changeQueryParams({
                  appointmentType:
                    queryParams.appointmentType === item.value
                      ? ""
                      : item.value,
                });
              }}
              showCheckBox={false}
              showRadio={true}
              loading={diseasStates.loader}
              loadingText="Loading Appointment Type"
              selectedItem={queryParams.appointmentType}
              borderBottom={false}
            />
          </div>
        </div>
        <div className={`product-view-container-width p-[2px] `}>
          <div className="w-full box-design min-h-full p-3">
            <CustomBreadCrub
              data={[
                {
                  label: "Home",
                  onClick: () => {
                    navigate("/");
                  },
                },
                {
                  label: "Consult a Doctor",
                  onClick: () => {
                    navigate("/consult-a-doctor");
                  },
                },
                ...(breadCrumbData.label
                  ? [
                      {
                        label: breadCrumbData.label,
                        onClick: () => {
                          navigate(
                            `/consult-a-doctor?${parseQueryParams({
                              [QueryPath.specialization]:
                                queryParams.specialization,
                              [QueryPath.specializationName]: allSpecialists
                                .filter((tm) =>
                                  queryParams.specialization.includes(tm._id)
                                )
                                .map((tm: any) => tm?.label),
                            })}`
                          );
                        },
                      },
                    ]
                  : []),
                ...(search ? [{ label: search }] : []),
              ]}
            />
            <div className="w-full flex flex-col gap-3 mt-3">
              <InfinteScrollWrapper
                paddingBottom="!pb-0"
                isLoading={doctorsLoader}
                length={doctors.length}
                currentPage={queryParams.page}
                totalPages={paginated.totalPages}
                onLoadMore={(page) => {
                  // For load more, we need to append data, not replace
                  let updatedParams = { ...queryParams, page: page };
                  setQueryParams(updatedParams);
                  fetchDoctors(updatedParams, true); // true = append data
                }}
                isLoadButton={true}
                noDataOptions={{
                  text2: "Doctor Not Found",
                }}
              >
                {doctors.map((item) => {
                  return (
                    <DoctorViewCard
                      key={"Doctor Card " + item?._id}
                      doctor={{
                        id: item?.userId,
                        name:
                          capitalize(item?.customersDetails?.firstName) +
                          " " +
                          capitalize(item?.customersDetails?.lastName),
                        specialty: item?.specializationDetails.map(
                          (tm: { name: string }) => tm?.name
                        ),
                        qualification: item?.providersDetails?.education
                          ?.map((v: any) => v?.course)
                          ?.join(", "),
                        experience: item?.providersDetails?.workExperience,
                        reviews: item?.allReviews?.[0]?.reviews?.length || 0,
                        rating: item?.customersDetails?.averageStars || 0,
                        // price: Math.min(10000000000, 1000000000,2000000000),
                        price: GetDoctorPrice(item?.feesArray, item?.providersDetails?.availability),
                        currency: CURRENCY,
                        image: item?.customersDetails?.coverPhoto,
                        isFavorite: item?.isLiked,
                      }}
                      onLikeProduct={(id, isFavorite) => {
                        console.log("Doctor Like Changed", id, isFavorite);
                        let updatedStatusData = doctors.map((v) => {
                          if (v?.userId === id) {
                            return {
                              ...v,
                              isLiked: isFavorite,
                            };
                          }
                          return v;
                        });
                        setDoctors(updatedStatusData);
                      }}
                    />
                  );
                })}
              </InfinteScrollWrapper>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultADoctorPage;
